import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { useSearchParams } from "react-router-dom";
import { Content } from "../utils/types";
import { contentService } from "../services/contentService";
import { useAuthContext } from "../components/AuthContext";

interface UseContentModalReturn {
  showContentsModal: string | null;
  setShowContentsModal: Dispatch<SetStateAction<string | null>>;
  isLoadingVideo: boolean;
}

export function useContentModal(
  purchasedContents: Content[],
  unpurchasedContents: Content[] = [],
): UseContentModalReturn {
  const [searchParams] = useSearchParams();
  const { isSubscribed } = useAuthContext();
  const [showContentsModal, setShowContentsModal] = useState<string | null>(
    null,
  );
  const [isLoadingVideo, setIsLoadingVideo] = useState<boolean>(false);

  // 全コンテンツを統合
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allContents = [...purchasedContents, ...unpurchasedContents];

  // コンテンツモーダル表示制御
  useEffect(() => {
    if (searchParams.get("session_id") || searchParams.get("transactionId")) {
      return;
    }

    if (!searchParams.get("isPlaying")) {
      setShowContentsModal(null);
    } else {
      const contentId = searchParams.get("id");
      const chapter = Number(searchParams.get("chapter"));

      if (!contentId) return;

      const currentContent = allContents.find((c) => c.id === contentId);
      if (!currentContent) return;

      const targetChapter = currentContent.chapters.find(
        (ch) => ch.index === chapter,
      );
      if (!targetChapter) return;

      if (!targetChapter.videoUrl) {
        setIsLoadingVideo(true);

        // サブスクユーザーまたは購入済みコンテンツの場合は動画URL取得
        const isPurchasedContent = purchasedContents.some(
          (c) => c.id === contentId,
        );
        if (isSubscribed || isPurchasedContent) {
          contentService
            .getVideoUrl(currentContent, chapter)
            .then((url) => {
              targetChapter.videoUrl = url;
              setShowContentsModal(currentContent.id);
              setIsLoadingVideo(false);
            })
            .catch((error) => {
              console.error("Failed to load video URL:", error);
              setIsLoadingVideo(false);
            });
        } else {
          // 未購入かつ非サブスクの場合はサンプル動画URL
          const sampleUrl = `https://firebasestorage.googleapis.com/v0/b/${process.env.REACT_APP_FIREBASE_STORAGE_BUCKET}/o/contents%2Fsamples%2F${contentId}.mp4?alt=media`;
          targetChapter.videoUrl = sampleUrl;
          setShowContentsModal(currentContent.id);
          setIsLoadingVideo(false);
        }
      } else {
        setIsLoadingVideo(false);
        setShowContentsModal(currentContent.id);
      }
    }
  }, [searchParams, allContents, isSubscribed, purchasedContents]);

  return {
    showContentsModal,
    setShowContentsModal,
    isLoadingVideo,
  };
}
